/**
 * Tell a long-lived POS tab that a newer build has been deployed.
 *
 * A terminal keeps this page open for days. The service worker serves the
 * shell network-first, so a RELOAD picks up a new build, but nothing ever
 * caused a reload. On 2026-09-21 a shop was reading an old bundle's cart
 * summary, which showed Total 138.00 beside Pay 120.00 on a senior-discounted
 * sale. The till charged the right amount throughout; the screen did not.
 *
 * `version.json` is written by build.sh next to the bundle, so the check needs
 * no backend and works the same behind nginx.
 * It is fetched with cache:'no-store' AND a cache-busting query, because the
 * whole failure mode here is a cached response.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

const POLL_MS = 5 * 60 * 1000;
const BUILT_VERSION = process.env.REACT_APP_VERSION || 'dev';

// --- "is it safe to reload?" -------------------------------------------------
// A POS must NEVER reload mid-sale. Screens that hold unsaved work mark
// themselves busy; the banner only reloads on its own while nothing is busy.
// A module-level registry avoids threading context through the whole tree.
let busy = false;
const busySubscribers = new Set();

export function setAppBusy(next) {
  const value = !!next;
  if (value === busy) return;
  busy = value;
  busySubscribers.forEach((fn) => fn(value));
}

export function useAppBusy() {
  const [value, setValue] = useState(busy);
  useEffect(() => {
    busySubscribers.add(setValue);
    setValue(busy);
    return () => busySubscribers.delete(setValue);
  }, []);
  return value;
}

// --- the version check -------------------------------------------------------
export function useAppVersion({ pollMs = POLL_MS } = {}) {
  const [deployedVersion, setDeployedVersion] = useState(null);
  const startedAt = useRef(Date.now());

  const check = useCallback(async () => {
    try {
      const res = await fetch(`${process.env.PUBLIC_URL || ''}/version.json?t=${Date.now()}`, {
        cache: 'no-store',
      });
      if (!res.ok) return;
      const data = await res.json();
      if (data && data.version) setDeployedVersion(data.version);
    } catch {
      // Offline or the file is not there yet. A POS that cannot reach the
      // server has bigger problems than being one build behind, and this must
      // never surface an error to a cashier.
    }
  }, []);

  useEffect(() => {
    check();
    const id = setInterval(check, pollMs);
    const onFocus = () => check();
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(id);
      window.removeEventListener('focus', onFocus);
    };
  }, [check, pollMs]);

  // 'dev' means the bundle was built without a stamp; comparing against it
  // would nag on every poll.
  const updateAvailable =
    !!deployedVersion && BUILT_VERSION !== 'dev' && deployedVersion !== BUILT_VERSION;

  return {
    updateAvailable,
    builtVersion: BUILT_VERSION,
    deployedVersion,
    /** Bypass the HTTP cache for the shell, not just the SW. */
    reload: () => window.location.reload(true),
    msSinceLoad: () => Date.now() - startedAt.current,
  };
}
